import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, Building2, PlayCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMBLEM = "/ginius-emblem.png";

const F = (label, key, opts = {}) => ({ label, key, ...opts });
const CAMPI = [
  F("Nome dello Studio", "studio_nome", { req: true, col: 2 }),
  F("Referente", "referente", { req: true }),
  F("Email", "email", { req: true, type: "email" }),
  F("Telefono", "telefono", { type: "tel" }),
  F("Città", "citta", { req: true }),
  F("Condomìni gestiti (indicativo)", "num_condomini"),
];
const CAMPI_FISCALI = [
  F("Ragione sociale", "ragione_sociale", { req: true, col: 2 }),
  F("Partita IVA", "partita_iva", { req: true }),
  F("Codice fiscale", "codice_fiscale", { req: true }),
  F("Codice destinatario (SDI)", "codice_sdi"),
  F("PEC", "pec", { req: true, type: "email" }),
  F("Indirizzo sede", "indirizzo_sede", { req: true, col: 2 }),
  F("CAP", "cap", { req: true }),
  F("Provincia (sigla)", "provincia", { req: true, maxLength: 2 }),
];

export default function RichiediProva() {
  const navigate = useNavigate();
  const [v, setV] = useState({});
  const [privacy, setPrivacy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const set = (k, val) => setV((s) => ({ ...s, [k]: val }));

  const invia = async () => {
    setErr("");
    // Iter122fb-c · validazione client-side allineata ai `req:true` (backend usa min_length)
    const richiesti = [...CAMPI, ...CAMPI_FISCALI].filter((c) => c.req);
    const mancanti = richiesti.filter((c) => !((v[c.key] || "").toString().trim())).map((c) => c.label);
    if (mancanti.length) { setErr(`Compila i campi obbligatori: ${mancanti.join(", ")}.`); return; }
    if (!privacy) { setErr("È necessario accettare l'informativa privacy."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/richieste-prova`, { ...v, privacy_consenso: true });
      setDone(true);
    } catch (e) {
      setErr(e.response?.data?.detail || "Invio non riuscito. Riprova.");
    } finally { setBusy(false); }
  };

  const inputCls = "w-full h-12 border-2 border-slate-300 rounded-lg px-3 focus:border-brand outline-none transition-colors";

  const renderCampo = (c) => (
    <div key={c.key} className={c.col === 2 ? "sm:col-span-2" : ""}>
      <label className="block text-sm font-semibold text-slate-700 mb-1">
        {c.label}{c.req && <span className="text-red-600"> *</span>}
      </label>
      <input data-testid={`rp-${c.key}`} type={c.type || "text"} className={inputCls}
        value={v[c.key] || ""} onChange={(e) => set(c.key, e.target.value)} />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F5EAEC] py-8 px-4"
      style={{ backgroundImage: "radial-gradient(120% 80% at 100% 0%, rgba(122,25,61,0.10), transparent 60%)" }}>
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <img src={EMBLEM} alt="Ginius" className="w-12 h-12 rounded-xl" />
          <div>
            <div className="font-display text-2xl font-extrabold text-brand">Ginius</div>
            <div className="text-sm text-slate-500">by Amministrazioni Izzo</div>
          </div>
        </div>

        {done ? (
          <div data-testid="rp-success" className="bg-white rounded-2xl border-2 border-emerald-200 p-8 text-center">
            <CheckCircle2 className="w-16 h-16 text-emerald-600 mx-auto mb-4" />
            <h1 className="font-display text-2xl font-bold mb-2">Richiesta inviata!</h1>
            <p className="text-slate-600 max-w-md mx-auto">
              Grazie. Abbiamo ricevuto la tua richiesta di prova: lo Studio ti contatterà a breve
              per attivare il tuo accesso gratuito di 30 giorni.
            </p>
            <button data-testid="rp-vai-presentazione" onClick={() => navigate("/presentazione?p=studio_promo")}
              className="mt-6 inline-flex items-center gap-2 text-brand font-semibold underline underline-offset-4">
              <PlayCircle className="w-5 h-5" /> Rivedi la presentazione
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border-2 border-brand/15 p-6 sm:p-8 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
              <Building2 className="w-6 h-6 text-brand" />
              <h1 className="font-display text-2xl font-extrabold">Richiedi la prova gratuita</h1>
            </div>
            <p className="text-slate-500 mb-6">
              30 giorni senza impegno, con uno spazio dati riservato. Compila il modulo: ti
              contatteremo per attivare il tuo accesso di prova.
            </p>

            <div className="grid sm:grid-cols-2 gap-4">
              {CAMPI.map(renderCampo)}
            </div>

            <h2 className="font-bold text-lg mt-7 mb-1">Dati fiscali <span className="font-normal text-slate-400 text-sm">(facoltativi, per future fatture)</span></h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {CAMPI_FISCALI.map(renderCampo)}
            </div>

            <div className="mt-5">
              <label className="block text-sm font-semibold text-slate-700 mb-1">Note</label>
              <textarea data-testid="rp-note" rows={3} className="w-full border-2 border-slate-300 rounded-lg px-3 py-2 focus:border-brand outline-none"
                value={v.note || ""} onChange={(e) => set("note", e.target.value)} />
            </div>

            <label className="flex items-start gap-3 mt-5 cursor-pointer">
              <input data-testid="rp-privacy" type="checkbox" className="mt-1 w-5 h-5 accent-brand"
                checked={privacy} onChange={(e) => setPrivacy(e.target.checked)} />
              <span className="text-sm text-slate-600">
                Acconsento al trattamento dei miei dati per essere ricontattato in merito alla richiesta di prova,
                ai sensi del Regolamento (UE) 2016/679 (GDPR). <span className="text-red-600">*</span>
              </span>
            </label>

            {err && <div data-testid="rp-error" className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}</div>}

            <button data-testid="rp-invia" onClick={invia} disabled={busy}
              className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-brand text-white font-bold text-lg px-8 py-3.5 rounded-full hover:bg-brand-dark transition-colors disabled:opacity-60">
              {busy ? <><Loader2 className="w-5 h-5 animate-spin" /> Invio…</> : "Invia la richiesta"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
